/**
 * Service for Database Assistant API operations
 * Provides natural language database querying capabilities
 */

import { makeAuthenticatedRequest } from './backendApiService';

const DB_ASSISTANT_BASE_URL = '/api/v1/db-assistant';

/**
 * Database Assistant Service Class
 */
class DatabaseAssistantService {
    /**
     * Ask a question about the database in natural language
     * @param {string} accessToken - JWT access token
     * @param {string} question - Natural language question (required, non-empty)
     * @param {string} [sessionId] - Optional session ID for maintaining conversation context
     * @param {boolean} [skipConfirmation] - Set to true to bypass clarification step
     * @param {string} [pendingQueryId] - ID of pending query to execute (for confirming clarifications)
     * @returns {Promise<Object>} Response with answer, SQL query, results, and clarification data
     * @example
     * const response = await askQuestion(token, "Is Kara Co a customer?");
     * // Returns: { success: true, answer: "Yes...", sqlQuery: "SELECT...", queryResult: {...}, needsConfirmation: false }
     * @example
     * // Question needing clarification
     * const response = await askQuestion(token, "List clients starting with a", "session-123");
     * // Returns: { success: true, needsConfirmation: true, clarification: "...", pendingQueryId: "pending_..." }
     * @example
     * // Confirming a clarification
     * const response = await askQuestion(token, "yes, that's correct", "session-123", false, "pending_...");
     */
    async askQuestion(accessToken, question, sessionId, skipConfirmation = false, pendingQueryId = null) {
        if (!question || !question.trim()) {
            throw new Error('Question cannot be empty');
        }

        try {
            const requestBody = { question: question.trim() };
            if (sessionId) {
                requestBody.sessionId = sessionId;
            }
            if (skipConfirmation) {
                requestBody.skipConfirmation = true;
            }
            if (pendingQueryId) {
                requestBody.pendingQueryId = pendingQueryId;
            }

            const response = await makeAuthenticatedRequest(
                accessToken,
                `${DB_ASSISTANT_BASE_URL}/ask`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody)
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to ask database question:', error);
            throw error;
        }
    }

    /**
     * Get example questions categorized by type
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Object with categories containing example questions
     * @example
     * const examples = await getExamples(token);
     * // Returns: { categories: { "Client Queries": [...], "Sample Queries": [...] } }
     */
    async getExamples(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${DB_ASSISTANT_BASE_URL}/examples`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch example questions:', error);
            throw error;
        }
    }

    /**
     * Clear the database schema cache (Admin only)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<string>} Success message
     */
    async clearCache(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${DB_ASSISTANT_BASE_URL}/clear-cache`,
                {
                    method: 'POST'
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to clear schema cache:', error);
            throw error;
        }
    }

    /**
     * Clear conversation history for a specific session
     * @param {string} accessToken - JWT access token
     * @param {string} sessionId - Session ID to clear
     * @returns {Promise<string>} Success message
     */
    async clearConversation(accessToken, sessionId) {
        if (!sessionId) {
            throw new Error('Session ID is required');
        }

        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${DB_ASSISTANT_BASE_URL}/clear-conversation`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ sessionId })
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to clear conversation:', error);
            throw error;
        }
    }

    /**
     * Clear all conversation histories (Admin only)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<string>} Success message
     */
    async clearAllConversations(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${DB_ASSISTANT_BASE_URL}/clear-all-conversations`,
                {
                    method: 'POST'
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to clear all conversations:', error);
            throw error;
        }
    }
}

// Export singleton instance
const databaseAssistantService = new DatabaseAssistantService();
export default databaseAssistantService;
